import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Chip, IconButton, Paper, Stack, TextField, ToggleButton,
    ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import WaterIcon from '@mui/icons-material/Water';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    useHydroCiselnikQuery, useHydroConfigQuery, useUpdateHydroConfigMutation,
} from '../../../api/JsdhNotifikaceService/service/MeteoMutations';
import type {
    HydroStanice, HydroStaniceEntry, MeteoReaction,
} from '../../../api/JsdhNotifikaceService/generated/models/Meteo';

const REAKCE_OPTIONS: { value: MeteoReaction; label: string }[] = [
    { value: 'NONE', label: 'Ignorovat' },
    { value: 'NOTIFY', label: 'Upozornit' },
    { value: 'ALARM', label: 'Vyhlásit' },
];

const DEFAULT_REAKCE = (): { reakceSpa1: MeteoReaction; reakceSpa2: MeteoReaction; reakceSpa3: MeteoReaction } => ({
    reakceSpa1: 'NONE',
    reakceSpa2: 'NOTIFY',
    reakceSpa3: 'ALARM',
});

/**
 * HO-94 E4 — Hydro per-stanice SPA varování sekce. Samostatná od ORP-CAP
 * sekce, vlastní save flow. Klient si vybere stanice (autocomplete z chmu-service
 * číselníku) a per stanice nastaví reakce per SPA stupeň 1/2/3.
 *
 * <p>Důležité pro hasiče: pohotovost se vyhlašuje až po 2 po sobě jdoucích
 * měřeních nad prahem (debounce false-positive, ČHMÚ data jsou nevalidovaná).
 */
const MeteoHydroSection: React.FC = () => {
    const ciselnikQuery = useHydroCiselnikQuery();
    const configQuery = useHydroConfigQuery();
    const updateMutation = useUpdateHydroConfigMutation();

    const [entries, setEntries] = useState<HydroStaniceEntry[]>([]);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (configQuery.data) {
            setEntries(configQuery.data.stanice ?? []);
            setDirty(false);
        }
    }, [configQuery.data]);

    const ciselnikByObjId = useMemo(() => {
        const m = new Map<string, HydroStanice>();
        (ciselnikQuery.data ?? []).forEach(s => m.set(s.obj_id, s));
        return m;
    }, [ciselnikQuery.data]);

    const selectedStations = useMemo(
        () => entries
            .map(e => ciselnikByObjId.get(e.objId))
            .filter((s): s is HydroStanice => s != null),
        [entries, ciselnikByObjId]);

    const tooMany = entries.length > 100;

    const handleSelectionChange = (newSelected: HydroStanice[]) => {
        // Při přidání: doplň výchozí reakce. Při odebrání: drop z entries.
        const newObjIds = new Set(newSelected.map(s => s.obj_id));
        const kept = entries.filter(e => newObjIds.has(e.objId));
        const existingIds = new Set(kept.map(e => e.objId));
        const added: HydroStaniceEntry[] = newSelected
            .filter(s => !existingIds.has(s.obj_id))
            .map(s => ({ objId: s.obj_id, ...DEFAULT_REAKCE() }));
        setEntries([...kept, ...added]);
        setDirty(true);
    };

    const handleReakceChange = (objId: string, key: keyof Pick<HydroStaniceEntry, 'reakceSpa1' | 'reakceSpa2' | 'reakceSpa3'>, value: MeteoReaction) => {
        setEntries(prev => prev.map(e => e.objId === objId ? { ...e, [key]: value } : e));
        setDirty(true);
    };

    const handleRemove = (objId: string) => {
        setEntries(prev => prev.filter(e => e.objId !== objId));
        setDirty(true);
    };

    const handleSave = () => {
        updateMutation.mutate({ stanice: entries }, {
            onSuccess: () => setDirty(false),
        });
    };

    if (ciselnikQuery.isLoading || configQuery.isLoading) {
        return <Paper sx={{ p: 3 }}><Typography>Načítám hydro konfiguraci…</Typography></Paper>;
    }

    return (
        <Paper sx={{ p: 3, mt: 3 }}>
            <Stack spacing={3}>
                <Typography variant="h6">
                    <WaterIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Hydrologická varování (per-stanice SPA)
                </Typography>

                <Alert severity="info">
                    Per-stanice povodňová pohotovost podle Stupňů Povodňové Aktivity (SPA).
                    <strong> Pohotovost se vyhlašuje až po 2 po sobě jdoucích měřeních nad prahem</strong>
                    {' '}(debounce false-positive — ČHMÚ data jsou operativní nevalidovaná).
                </Alert>

                <Autocomplete
                    multiple
                    options={ciselnikQuery.data ?? []}
                    value={selectedStations}
                    onChange={(_, v) => handleSelectionChange(v as HydroStanice[])}
                    getOptionLabel={(o) => `${o.nazev} (${o.tok}) — ${o.spa_typ}`}
                    isOptionEqualToValue={(o, v) => o.obj_id === v.obj_id}
                    renderTags={() => null}
                    renderInput={(params) => (
                        <TextField {...params}
                            label="Vybrané hydro stanice"
                            placeholder="Vyhledat stanici…" />
                    )}
                />

                {tooMany && (
                    <Alert severity="warning">
                        Vybráno {entries.length} stanic — maximum je 100 per jednotka.
                    </Alert>
                )}

                {entries.length === 0 ? (
                    <Alert severity="info">Žádné hydro stanice nejsou sledované.</Alert>
                ) : (
                    <Stack spacing={1}>
                        {entries.map((e) => {
                            const station = ciselnikByObjId.get(e.objId);
                            return (
                                <Paper key={e.objId} variant="outlined" sx={{ p: 2 }}>
                                    <Stack direction={{ xs: 'column', md: 'row' }}
                                           spacing={2}
                                           alignItems={{ md: 'center' }}>
                                        <Box sx={{ minWidth: 240 }}>
                                            <Typography variant="subtitle1">
                                                {station?.nazev ?? e.objId}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {station?.tok} •
                                                {station?.spa_typ === 'H' ? ' vodní stav (cm)' : ' průtok (m³/s)'}
                                            </Typography>
                                            {station?.spa1 != null && station?.spa2 != null && station?.spa3 != null && (
                                                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                                                    Prahy: SPA1={station.spa1} SPA2={station.spa2} SPA3={station.spa3} {station.jednotka}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                            <SpaReactionPicker
                                                label="🟡 1.SPA Bdělost"
                                                value={e.reakceSpa1}
                                                onChange={(v) => handleReakceChange(e.objId, 'reakceSpa1', v)}
                                            />
                                            <SpaReactionPicker
                                                label="🟠 2.SPA Pohotovost"
                                                value={e.reakceSpa2}
                                                onChange={(v) => handleReakceChange(e.objId, 'reakceSpa2', v)}
                                            />
                                            <SpaReactionPicker
                                                label="🔴 3.SPA Ohrožení"
                                                value={e.reakceSpa3}
                                                onChange={(v) => handleReakceChange(e.objId, 'reakceSpa3', v)}
                                            />
                                        </Stack>
                                        <Box sx={{ marginLeft: 'auto' }}>
                                            <Tooltip title="Odebrat stanici">
                                                <IconButton onClick={() => handleRemove(e.objId)} aria-label="Odebrat">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Stack>
                                </Paper>
                            );
                        })}
                    </Stack>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        disabled={!dirty || updateMutation.isPending || tooMany}
                        onClick={handleSave}
                    >
                        {updateMutation.isPending ? 'Ukládám…' : 'Uložit hydro konfiguraci'}
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );
};

const SpaReactionPicker: React.FC<{
    label: string;
    value: MeteoReaction;
    onChange: (v: MeteoReaction) => void;
}> = ({ label, value, onChange }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
        <ToggleButtonGroup
            exclusive
            value={value}
            size="small"
            onChange={(_, v) => { if (v !== null) onChange(v); }}>
            {REAKCE_OPTIONS.map(o => (
                <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>
            ))}
        </ToggleButtonGroup>
    </Box>
);

export default MeteoHydroSection;
